import type { Account, AccountClass, AccountNode } from '@taler/api-client'
import { type Locale, resolveLocalized } from '@taler/domain'

export interface AccountLookup {
  readonly label: string
  readonly code: string
  readonly accountClass: AccountClass
  readonly icon: string
  readonly color: string
  readonly isHidden: boolean
}

function label(account: Account, locale: Locale): string {
  return resolveLocalized(account.names, locale)?.name ?? account.code
}

/** Flattens the tree the accounts page already fetches into an id-keyed lookup - the server
 * side of a reconciliation session sends only ids and figures, never names or icons. */
export function buildAccountLookup(
  tree: readonly AccountNode[],
  locale: Locale,
): ReadonlyMap<string, AccountLookup> {
  const lookup = new Map<string, AccountLookup>()
  const walk = (nodes: readonly AccountNode[]): void => {
    for (const node of nodes) {
      lookup.set(node.account.id, {
        label: label(node.account, locale),
        code: node.account.code,
        accountClass: node.account.accountClass,
        icon: node.account.icon,
        color: node.account.color,
        isHidden: node.account.hidden,
      })
      walk(node.children)
    }
  }
  walk(tree)
  return lookup
}
